import {
    normalizeMeasureAttributes,
    representationForDefinition,
    resolveMeasureDefinition,
    type ConstructionSystem,
    type MeasureForm,
} from "@treegram/viewer-core";
import MeasureShape from "@/pages/takeoff/context/project-measure/models/measure-shape.ts";
import MeasureType from "@/pages/takeoff/context/project-measure/models/measure-type.ts";
import editMeasureTypeAction from "./edit-measure-type-action";

interface AddMeasureTypeActionParams {
    formData: FormData;
    predefinedShapes: MeasureShape[];
    projectMeasureTypes: MeasureType[];
    addMeasureType: (
        measureRepresentationId: string,
        name: string,
        dataInformations: string,
    ) => Promise<MeasureType | null>;
}

interface AddMeasureTypeActionResponse {
    success?: boolean;
    message?: string;
    addedMeasureType?: MeasureType;
}

const addMeasureTypeAction = async ({
    formData,
    predefinedShapes,
    projectMeasureTypes,
    addMeasureType,
}: AddMeasureTypeActionParams): Promise<AddMeasureTypeActionResponse> => {
    const name = ((formData.get("name") as string) || "").trim();
    const color = formData.get("color") as string;
    const hasAdditionalConstruction = formData.get("hasAdditionalConstruction") === "true";
    const measureRepresentationIdFromForm = formData.get("measureRepresentationId") as string;
    const shapeName = formData.get("shape") as string;

    const selectedShape = predefinedShapes.find((s) => s.name === shapeName);

    if (name === "" || !selectedShape || !color) {
        return { message: "Veuillez remplir tous les champs." };
    }

    const system = (formData.get("constructionSystem") ||
        (hasAdditionalConstruction
            ? resolveMeasureDefinition(measureRepresentationIdFromForm).system
            : "none")) as ConstructionSystem;
    let measureRepresentationId: string;
    try {
        measureRepresentationId = representationForDefinition(shapeName as MeasureForm, system).id;
    } catch {
        return { message: "Système constructif incompatible avec la forme." };
    }
    if (
        !selectedShape.representations.some(
            (rep) => rep.measureRepresentationId.toLowerCase() === measureRepresentationId.toLowerCase(),
        )
    )
        return { message: "Représentation de métré introuvable." };
    const candidate = new MeasureType("", measureRepresentationId, selectedShape.measureShapeId, name, {
        constructionSystem: system,
        measureForm: shapeName,
    });
    const result = await editMeasureTypeAction({ measureType: candidate, formData });
    if (!result.success || !result.updatedMeasureType) return { message: result.message };
    const { dataInformations } = result.updatedMeasureType;

    if (!measureRepresentationId) {
        return { message: "Représentation de métré introuvable." };
    }

    if (
        projectMeasureTypes.find(
            (mt) => mt.name === name && mt.measureRepresentationId === measureRepresentationId,
        )
    ) {
        return { message: "Le type indiqué existe déjà dans votre projet." };
    }

    try {
        const type = await addMeasureType(
            measureRepresentationId,
            name,
            JSON.stringify(normalizeMeasureAttributes(measureRepresentationId, dataInformations)),
        );
        return { success: type != null, addedMeasureType: type ?? undefined };
    } catch (e) {
        return { message: e instanceof Error ? e.message : "Une erreur est survenue." };
    }
};

export default addMeasureTypeAction;
